import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useAssets } from '../../contexts/AssetsContext';

const AssetManagement = () => {
  const { assets, addAsset, deleteAsset } = useAssets();
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: '',
    value: '',
    ownerId: '',
    status: 'normal',
    location: { lat: 0, lng: 0 }
  });

  const handleOpenDialog = (mode, asset = null) => {
    setDialogMode(mode);
    if (asset) {
      setSelectedAsset(asset);
      setFormData({
        name: asset.name,
        description: asset.description,
        type: asset.type,
        value: asset.value,
        ownerId: asset.ownerId,
        status: asset.status,
        location: { ...asset.location }
      });
    } else {
      setSelectedAsset(null);
      setFormData({
        name: '',
        description: '',
        type: '',
        value: '',
        ownerId: '',
        status: 'normal',
        location: { lat: 0, lng: 0 }
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'value' ? Number(value) : value
    });
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      location: {
        ...formData.location,
        [name]: Number(value)
      }
    });
  };

  const handleSubmit = () => {
    // Validate required fields
    if (!formData.name || !formData.type) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }
    
    if (dialogMode === 'add') {
      // Add new asset
      const newAsset = addAsset(formData);
      console.log('Added new asset:', newAsset);
      
      setSnackbar({
        open: true,
        message: 'Asset added successfully',
        severity: 'success'
      });
    } else if (dialogMode === 'edit') {
      // In a real app, this would update the asset
      console.log('MOCK: Updating asset', selectedAsset.id, formData);
      
      setSnackbar({
        open: true,
        message: 'Asset updated successfully',
        severity: 'success'
      });
    }
    
    // Close the dialog
    handleCloseDialog();
  };

  const handleDelete = (assetId) => {
    // Delete the asset using the context function
    deleteAsset(assetId);
    
    setSnackbar({
      open: true,
      message: 'Asset deleted successfully',
      severity: 'success'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          Assets ({assets.length})
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog('add')}
        >
          Add Asset
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="assets table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Owner</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assets.map((asset) => (
              <TableRow
                key={asset.id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell component="th" scope="row">
                  {asset.name}
                </TableCell>
                <TableCell>{asset.type}</TableCell>
                <TableCell>{formatCurrency(asset.value)}</TableCell>
                <TableCell>
                  <Chip 
                    label={asset.status.toUpperCase()} 
                    color={asset.status === 'normal' ? 'success' : 'warning'} 
                    size="small" 
                  />
                </TableCell>
                <TableCell>{asset.ownerId}</TableCell>
                <TableCell>{new Date(asset.lastUpdated).toLocaleString()}</TableCell>
                <TableCell align="right">
                  <IconButton 
                    aria-label="view" 
                    size="small"
                    onClick={() => handleOpenDialog('view', asset)}
                  >
                    <ViewIcon />
                  </IconButton>
                  <IconButton 
                    aria-label="edit" 
                    size="small"
                    onClick={() => handleOpenDialog('edit', asset)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    aria-label="delete" 
                    size="small"
                    onClick={() => handleDelete(asset.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Add/Edit/View Asset Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Asset' : 
           dialogMode === 'edit' ? 'Edit Asset' : 'View Asset Details'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                name="name"
                label="Asset Name"
                fullWidth
                value={formData.name}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="type"
                label="Asset Type"
                fullWidth
                value={formData.type}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                select
                required
              >
                <MenuItem value="necklace">Necklace</MenuItem>
                <MenuItem value="ring">Ring</MenuItem>
                <MenuItem value="earrings">Earrings</MenuItem>
                <MenuItem value="watch">Watch</MenuItem>
                <MenuItem value="bracelet">Bracelet</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="value"
                label="Value (USD)"
                fullWidth
                type="number"
                value={formData.value}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                InputProps={{
                  startAdornment: <Typography>$</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="status"
                label="Status"
                fullWidth
                value={formData.status}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                select
              >
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="warning">Warning</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="ownerId"
                label="Owner ID"
                fullWidth
                value={formData.ownerId}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Location
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    name="lat"
                    label="Latitude"
                    fullWidth
                    type="number"
                    value={formData.location.lat}
                    onChange={handleLocationChange}
                    disabled={dialogMode === 'view'}
                    inputProps={{ step: 0.000001 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    name="lng"
                    label="Longitude"
                    fullWidth
                    type="number"
                    value={formData.location.lng}
                    onChange={handleLocationChange}
                    disabled={dialogMode === 'view'}
                    inputProps={{ step: 0.000001 }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {dialogMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogMode !== 'view' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogMode === 'add' ? 'Add Asset' : 'Save Changes'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssetManagement;
